// BookshelfComponent.js (React Component)

import React, { useState, useEffect, useRef } from "react";
import "./BookshelfComponent.css";
import colcBook from "./assets/colcBook.png";
import ccswBook from "./assets/ccswBook.png";
import mmuBook from "./assets/mmuBook.png";

const Book = ({
  spineColor,
  title,
  coverImage,
  isFocused,
  index,
  setFocusedBook,
}) => {
  const handleMouseEnter = () => {
    setFocusedBook(index);
  };

  const handleMouseLeave = () => {
    setFocusedBook(null);
  };

  const bookRef = useRef(null); // Create a ref for the book container

  useEffect(() => {
    if (bookRef.current) {
      bookRef.current.dataset.index = index;
    }
  }, [index]);

  return (
    <div
      className={`book-container ${isFocused ? "focused" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={() => setFocusedBook(index)} // Add touch support
      ref={bookRef} // Attach the ref
    >
      <div className="book-spine" style={{ backgroundColor: spineColor }}>
        <h2 className="book-title">{title}</h2>
        <div className="book-spine-shadow"></div>
      </div>
      <div className="book-cover">
        <img
          alt={`${title} Cover`}
          src={coverImage}
          className="book-cover-image"
        />
        <span className="book-cover-gradient"></span>
      </div>
      <div className="book-shadow"></div>
    </div>
  );
};

const BookshelfComponent = () => {
  const books = [
    {
      title: "Creative Media Practice",
      spineColor: "#b991e8",
      coverImage: ccswBook,
    },
    {
      title: "Photography",
      spineColor: "#61a66e",
      coverImage: colcBook,
    },
    {
      title: "Future Media Production",
      spineColor: "#bc346e",
      coverImage: mmuBook,
    },
  ];

  const [focusedBook, setFocusedBook] = useState(null);
  const bookshelfRef = useRef(null); // Ref for the bookshelf container
  const [touchStartX, setTouchStartX] = useState(null); // Track touch start X position

  useEffect(() => {
    const updateBookPositions = () => {
      if (!bookshelfRef.current) return;

      const bookContainers =
        bookshelfRef.current.querySelectorAll(".book-container");

      // Remove "left-shifted" and "right-shifted" classes from all books
      bookContainers.forEach((book) => {
        book.classList.remove("left-shifted", "right-shifted");
      });

      if (focusedBook !== null) {
        // Check for null, not just truthiness
        const focusedIndex = focusedBook;
        bookContainers.forEach((book, index) => {
          if (index < focusedIndex) {
            book.classList.add("left-shifted");
          } else if (index > focusedIndex) {
            book.classList.add("right-shifted");
          }
        });
      }
    };

    updateBookPositions(); // Call on initial render and when focusedBook changes

    // No event listeners are attached here directly.  The event handling is done within the <Book /> component
  }, [focusedBook]); // Dependency array: re-run when focusedBook changes

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (touchStartX === null) return;

    const touchCurrentX = e.touches[0].clientX;
    const diffX = touchStartX - touchCurrentX;

    if (Math.abs(diffX) > 75) {
      // Adjust sensitivity as needed
      if (diffX > 0 && focusedBook < books.length - 1) {
        setFocusedBook((prev) => prev + 1);
      } else if (diffX < 0 && focusedBook > 0) {
        setFocusedBook((prev) => prev - 1);
      }
      setTouchStartX(null); // Reset touch start to prevent continuous swiping
    }
  };

  const handleTouchEnd = () => {
    setTouchStartX(null); // Clean up on touch end
  };

  return (
    <div
      className="bookshelf-container"
      ref={bookshelfRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <h1 className="bookshelf-header">
        Knowledge
        <br />
        Library
      </h1>
      <div className="books-container">
        {books.map((book, index) => (
          <Book
            key={index}
            title={book.title}
            spineColor={book.spineColor}
            coverImage={book.coverImage}
            isFocused={focusedBook === index}
            setFocusedBook={setFocusedBook}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};

export default BookshelfComponent;
